import {useEffect, useMemo} from 'react';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {
  FULL_FIDELITY_MODES,
  type FullFidelityMode,
} from '../../../components/WorkspaceDrawer/Settings/types';
import {
  type BooleanDynamicConfig,
  type NumericDynamicConfig,
  useViewerOrgsBooleanDynamicConfig,
  useViewerOrgsNumericDynamicConfig,
  useViewerOrgsStringDynamicConfig,
} from '../../useDynamicConfig';

/**
 * Store-backed translations of the hooks in `util/rampFeatureFlags/
 * linePlotDynamicConfigs.ts`. Each evaluator hook preserves the original
 * logic - Statsig dynamic-config fetching, cross-org ranking/merging, and
 * error/loading fallbacks - and is called once per route by
 * `useInitializeLinePlotDynamicConfigStore` so every panel reads O(1) fields.
 *
 * Only the resolved values are stored. Consumers never needed the loading /
 * error state (pre-hydration defaults already match the "still loading" and
 * "errored" behavior), so we keep the store surface minimal.
 *
 * The originals in `linePlotDynamicConfigs.ts` now just read this store;
 * migrate call sites incrementally by swapping imports.
 */

export const PRIMARY_LINE_TYPES = ['full-fidelity', 'min-max-last'] as const;
export type PrimaryLineType = (typeof PRIMARY_LINE_TYPES)[number];

const VALID_PRIMARY_LINE_TYPES: ReadonlySet<PrimaryLineType> = new Set(
  PRIMARY_LINE_TYPES
);

/**
 * Caches the viewer-org dynamic-config reads as pre-merged primitive values
 * (one hydration per route). Readers do a single record lookup and no
 * per-render ranking work.
 */
type LinePlotDynamicConfigState = {
  primaryLineType: PrimaryLineType;
  isMinMaxLastModeEnabled: boolean;
  linePlotMaxRuns: number | undefined;
  fullScreenLinePlotMaxRuns: number | undefined;
  groupingDepth: number | undefined;
};

const INITIAL_LINE_PLOT_DYNAMIC_CONFIG_STATE: LinePlotDynamicConfigState = {
  primaryLineType: 'full-fidelity',
  isMinMaxLastModeEnabled: false,
  linePlotMaxRuns: undefined,
  fullScreenLinePlotMaxRuns: undefined,
  groupingDepth: undefined,
};

// Most consumers go through the narrow reader hooks in
// `linePlotDynamicConfigs.ts`. Other stores can read this store directly to
// derive their route-level snapshot.
export const useLinePlotDynamicConfigStore =
  create<LinePlotDynamicConfigState>()(
    devtools(() => INITIAL_LINE_PLOT_DYNAMIC_CONFIG_STATE, {
      name: 'LinePlotDynamicConfigStore',
    })
  );

export const useInitializeLinePlotDynamicConfigStore = () => {
  const primaryLineType = usePrimaryLineType();
  const {value: isMinMaxLastModeEnabled} = useEnableMinMaxLastMode();
  const {value: linePlotMaxRuns} = useLinePlotMaxRunsDynamicConfig();
  const {value: fullScreenLinePlotMaxRuns} =
    useFullScreenLinePlotMaxRunsDynamicConfig();
  const {value: groupingDepth} = useLinePlotGroupingDepthDynamicConfig();

  // This is a valid Effect: it synchronizes React hook outputs into the
  // external Zustand store after render.
  // https://react.dev/reference/react/useEffect
  useEffect(() => {
    useLinePlotDynamicConfigStore.setState({
      primaryLineType,
      isMinMaxLastModeEnabled,
      linePlotMaxRuns,
      fullScreenLinePlotMaxRuns,
      groupingDepth,
    });
  }, [
    primaryLineType,
    isMinMaxLastModeEnabled,
    linePlotMaxRuns,
    fullScreenLinePlotMaxRuns,
    groupingDepth,
  ]);
};

/**
 * Returns the primary line type configured across all of the viewer's
 * organizations via the `history_line_feature_flags` Statsig dynamic config.
 *
 * The ranking of line types is:
 * - min-max-last
 * - full-fidelity (default)
 *
 * If any org has the highest priority line type, that wins.
 */
export const usePrimaryLineType = (): PrimaryLineType => {
  const {values, error} = useViewerOrgsStringDynamicConfig(
    'history_line_feature_flags',
    'primaryLineType',
    VALID_PRIMARY_LINE_TYPES
  );

  const value = useMemo(() => {
    if (values.has('min-max-last')) {
      return 'min-max-last';
    }
    return 'full-fidelity';
  }, [values]);

  if (error) {
    return 'full-fidelity';
  }

  return value;
};

/**
 * Returns whether min/max/last bin visualization mode is enabled via the
 * `enableMinMaxLastMode` flag on the `history_line_feature_flags` Statsig
 * dynamic config. Enabled for the viewer if ANY of their orgs has it set
 * to `true`.
 */
export const useEnableMinMaxLastMode = (): BooleanDynamicConfig => {
  return useViewerOrgsBooleanDynamicConfig(
    'history_line_feature_flags',
    'enableMinMaxLastMode'
  );
};

export const useLinePlotGroupingDepthDynamicConfig =
  (): NumericDynamicConfig => {
    return useViewerOrgsNumericDynamicConfig(
      'history_line_feature_flags',
      'groupingDepth',
      'max'
    );
  };

/**
 * Derives a `PrimaryLineType` from a `fullFidelityMode` setting value, gated
 * on `enableMinMaxLastMode`. Unlike `usePrimaryLineType` (which aggregates an
 * org-level Statsig config across the viewer's organizations), this hook's
 * input is the `fullFidelityMode` the caller already holds in hand - typically
 * the cascaded panel/workspace setting. Only two values are produced:
 * `'min-max-last'` when the gate is open AND `fullFidelityMode` is explicitly
 * `'min-max-last'`, and `'full-fidelity'` otherwise.
 */
export const useFullFidelityModeLineType = (
  fullFidelityMode: FullFidelityMode | undefined
): PrimaryLineType => {
  const {value: enableMinMaxLastMode} = useEnableMinMaxLastMode();
  if (
    enableMinMaxLastMode &&
    fullFidelityMode === FULL_FIDELITY_MODES.MinMaxLast
  ) {
    return 'min-max-last';
  }
  return 'full-fidelity';
};

/**
 * Returns the maximum `line_plot_max_runs` value configured across all of
 * the viewer's organizations via the `line_plot_increased_max_runs` Statsig
 * dynamic config. Uses the 'max' strategy so the most permissive org wins.
 */
export const useLinePlotMaxRunsDynamicConfig = (): NumericDynamicConfig => {
  return useViewerOrgsNumericDynamicConfig(
    'line_plot_increased_max_runs',
    'line_plot_max_runs',
    'max'
  );
};

/**
 * Returns the maximum `line_plot_max_runs` value for full-screen panels,
 * configured via the `full_screen_line_plot_increased_max_runs` Statsig
 * dynamic config. Uses the 'max' strategy so the most permissive org wins.
 */
export const useFullScreenLinePlotMaxRunsDynamicConfig =
  (): NumericDynamicConfig => {
    return useViewerOrgsNumericDynamicConfig(
      'full_screen_line_plot_increased_max_runs',
      'line_plot_max_runs',
      'max'
    );
  };
